import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, Button, CircularProgress, MenuItem, Select, InputLabel, FormControl, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { FaUser, FaEnvelope, FaComment, FaPhone, FaBuilding, FaIdCard, FaMapMarkerAlt } from 'react-icons/fa';

const QuoteForm = () => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clientType, setClientType] = useState('particulier');

  const categories = [
    'Pain libanais', 'Pains spéciaux', 'Brioches', 'Biscuits', 'Baklawa', 'Pâtisserie',
    'Sandwicherie', 'Traiteur', 'Épicerie orientale', 'Épicerie slave', 'Snacking', 'Cave à vin',
  ];

  const validateForm = (formData) => {
    const newErrors = {};
    if (!formData.get('name')) newErrors.name = 'Le nom est requis';
    if (!formData.get('email')) newErrors.email = 'L\'email est requis';
    if (clientType === 'société') {
      if (!formData.get('companyName')) newErrors.companyName = 'Le nom de la société est requis';
      if (!formData.get('siret')) newErrors.siret = 'Le numéro de SIRET est requis';
    }
    if (!formData.get('category')) newErrors.category = 'La catégorie est requise';
    if (!formData.get('localization')) newErrors.localization = 'La localisation est requise';
    if (!formData.get('message')) newErrors.message = 'Le message est requis';
    return newErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.target;
    const formData = new FormData(form);
    const validationErrors = validateForm(formData);
  
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
  
    setIsSubmitting(true);
    try {
      const response = await fetch('/', {
        method: 'POST',
        body: new URLSearchParams(formData).toString(),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
  
      if (response.ok) {
        navigate('/success');
      } else {
        console.error('Erreur lors de la soumission');
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px',
        maxWidth: { xs: '100%', sm: '600px' },
        margin: '50px auto',
        padding: { xs: '16px', sm: '0' },
      }}
    >
      <Typography
        variant="h4"
        component="h1"
        sx={{
          color: '#8B4513',
          fontWeight: 'bold',
          textAlign: 'center',
          marginBottom: '20px',
        }}
      >
        Demande de devis
      </Typography>

      <Box
        component="form"
        role="form"
        name="quote"
        method="POST"
        data-netlify="true"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          width: '100%',
        }}
      >
        <input type="hidden" name="form-name" value="quote" />
        <input type="hidden" name="clientType" value={clientType} /> {/* Champ caché pour clientType */}

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <TextField
            label="Nom & Prénom"
            name="name"
            variant="outlined"
            required
            fullWidth
            error={!!errors.name}
            helperText={errors.name}
            InputProps={{ startAdornment: <FaUser style={{ marginRight: '8px', color: '#8B4513' }} /> }}
            sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: errors.name ? 'red' : '#8B4513' }, '&:hover fieldset': { borderColor: '#8B4513' }, '&.Mui-focused fieldset': { borderColor: '#8B4513' } }, '& .MuiInputLabel-root': { color: '#8B4513' } }}
          />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
          <FormControl fullWidth>
            <InputLabel id="client-type-label">Type de client</InputLabel>
            <Select
              labelId="client-type-label"
              value={clientType}
              label="Type de client"
              onChange={(e) => setClientType(e.target.value)}
            >
              <MenuItem value="particulier">Particulier</MenuItem>
              <MenuItem value="société">Société</MenuItem>
            </Select>
          </FormControl>
        </motion.div>

        {clientType === 'société' && (
          <>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
              <TextField
                label="Nom de la société"
                name="companyName"
                variant="outlined"
                required
                fullWidth
                error={!!errors.companyName}
                helperText={errors.companyName}
                InputProps={{ startAdornment: <FaBuilding style={{ marginRight: '8px', color: '#8B4513' }} /> }}
                sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: errors.companyName ? 'red' : '#8B4513' }, '&:hover fieldset': { borderColor: '#8B4513' }, '&.Mui-focused fieldset': { borderColor: '#8B4513' } }, '& .MuiInputLabel-root': { color: '#8B4513' } }}
              />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}>
              <TextField
                label="Numéro de SIRET"
                name="siret"
                variant="outlined"
                required
                fullWidth
                error={!!errors.siret}
                helperText={errors.siret}
                InputProps={{ startAdornment: <FaIdCard style={{ marginRight: '8px', color: '#8B4513' }} /> }}
                sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: errors.siret ? 'red' : '#8B4513' }, '&:hover fieldset': { borderColor: '#8B4513' }, '&.Mui-focused fieldset': { borderColor: '#8B4513' } }, '& .MuiInputLabel-root': { color: '#8B4513' } }}
              />
            </motion.div>
          </>
        )}

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}>
          <TextField
            label="Email"
            name="email"
            type="email"
            variant="outlined"
            required
            fullWidth
            error={!!errors.email}
            helperText={errors.email}
            InputProps={{ startAdornment: <FaEnvelope style={{ marginRight: '8px', color: '#8B4513' }} /> }}
            sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: errors.email ? 'red' : '#8B4513' }, '&:hover fieldset': { borderColor: '#8B4513' }, '&.Mui-focused fieldset': { borderColor: '#8B4513' } }, '& .MuiInputLabel-root': { color: '#8B4513' } }}
          />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.5 }}>
          <TextField
            label="Téléphone"
            name="phone"
            type="tel"
            variant="outlined"
            fullWidth
            InputProps={{ startAdornment: <FaPhone style={{ marginRight: '8px', color: '#8B4513' }} /> }}
            sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#8B4513' }, '&:hover fieldset': { borderColor: '#8B4513' }, '&.Mui-focused fieldset': { borderColor: '#8B4513' } }, '& .MuiInputLabel-root': { color: '#8B4513' } }}
          />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.7 }}>
          <TextField
            label="Localisation"
            name="localization"
            variant="outlined"
            fullWidth
            error={!!errors.localization}
            helperText={errors.localization}
            InputProps={{ startAdornment: <FaMapMarkerAlt style={{ marginRight: '8px', color: '#8B4513' }} /> }}
            sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: errors.localization ? 'red' : '#8B4513' }, '&:hover fieldset': { borderColor: '#8B4513' }, '&.Mui-focused fieldset': { borderColor: '#8B4513' } }, '& .MuiInputLabel-root': { color: '#8B4513' } }}
          />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.6 }}>
          <FormControl fullWidth error={!!errors.category}>
            <InputLabel id="category-label">Catégorie</InputLabel>
            <Select
              labelId="category-label"
              name="category"
              label="Catégorie"
              required
            >
              {categories.map((cat, index) => (
                <MenuItem key={index} value={cat}>{cat}</MenuItem>
              ))}
            </Select>
            {errors.category && <p style={{ color: 'red', marginTop: '5px' }}>{errors.category}</p>}
          </FormControl>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.8 }}>
          <TextField
            label="Message"
            name="message"
            multiline
            rows={4}
            variant="outlined"
            required
            fullWidth
            error={!!errors.message}
            helperText={errors.message}
            InputProps={{ startAdornment: <FaComment style={{ marginRight: '8px', color: '#8B4513' }} /> }}
            sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: errors.message ? 'red' : '#8B4513' }, '&:hover fieldset': { borderColor: '#8B4513' }, '&.Mui-focused fieldset': { borderColor: '#8B4513' } }, '& .MuiInputLabel-root': { color: '#8B4513' } }}
          />
        </motion.div>

        <motion.div whileHover={{ scale: 1.05, boxShadow: '0 5px 15px rgba(139,69,19,0.3)' }} whileTap={{ scale: 0.95 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#8B4513',
              color: '#fff',
              padding: '12px 0',
              fontWeight: 'bold',
            }}
            fullWidth
          >
            {isSubmitting ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Envoyer la demande'}
          </Button>
        </motion.div>
      </Box>
    </Box>
  );
};

export default QuoteForm;